/**
 * The reference a payer types into their bank's "referencia" field so a wire
 * can be matched back to the checkout it pays for.
 *
 * WHY THIS EXISTS. A bank transfer arrives as a line on a statement: an amount,
 * a date, a payer name. None of those identify WHICH checkout it settles — two
 * tenants on the same plan wire the same amount, and the name on the account is
 * often a person where the tenant is a company. The receipt upload tells us a
 * payment was claimed; the reference is what ties the money to the row.
 *
 * DERIVED, NOT STORED, so it cannot drift from the checkout and needs no
 * column: the same checkout id always yields the same reference, on the portal
 * that shows it and on the backend that looks it up. THIS IS WHY IT LIVES IN
 * shared-utils — a second copy in either app is a reference that silently stops
 * matching the moment one side is edited.
 *
 * NOT A SECRET. It identifies a checkout; it does not authorise anything, and
 * the payer is meant to show it to their bank. Do not use it as a token.
 */
/**
 * Characters a human transcribes wrongly, folded to what they must have meant.
 *
 * ONLY NON-HEX LETTERS APPEAR HERE, and that restriction is load-bearing. The
 * reference is six characters of a UUID, so every real character is hex
 * (0-9, A-F). O, I and L cannot occur in a genuine reference, so seeing one
 * means the payer typed a letter for the digit it resembles — folding it
 * recovers the real value.
 *
 * Folding a character that CAN occur would do the opposite. An earlier version
 * mapped B→8 and S→5: B and 8 are both valid hex, so a checkout whose
 * reference contained B and another whose reference contained 8 in the same
 * position would collapse onto the same canonical string, and a receipt for
 * one would match the other. A fold is only safe when the source character is
 * impossible in a real reference.
 */
const CONFUSABLE = {
    O: "0",
    I: "1",
    L: "1",
};
/**
 * `FIS-A1B2C3` for checkout `a1b2c3d4-…`.
 *
 * SIX characters of the id. A checkout id is a UUID, so six hex digits is ~16.7
 * million values — far more than the number of open checkouts that could ever
 * be matched against at once, and short enough that someone types it correctly
 * into a banking app on a phone.
 *
 * Uppercased because bank reference fields usually are, and because a payer
 * reading it off a screen will uppercase it anyway.
 */
export function planCheckoutPaymentReference(checkoutId) {
    const compact = (checkoutId ?? "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (compact.length < 6)
        return "";
    return `FIS-${compact.slice(0, 6)}`;
}
/**
 * Turns what the payer actually wrote back into the canonical reference, so a
 * statement line can be looked up.
 *
 * Tolerant on purpose. The reference is copied by hand into a bank form and
 * comes back to us through a receipt image or a statement export, so it arrives
 * lowercased, without the prefix, with the dash as a space, or with an O typed
 * for a 0. Each of those is the same payment, and refusing to match it means a
 * human reconciles by hand — which is the work this is meant to remove.
 *
 * Returns "" when there is nothing reference-shaped in the input, so a caller
 * can tell "no reference given" from "a reference that does not match".
 */
export function normalisePlanCheckoutPaymentReference(raw) {
    const upper = (raw ?? "").trim().toUpperCase();
    /**
     * SEARCH for the marker, never assume it leads. A bank's "concepto" field
     * comes back as free text and the payer writes around the reference as often
     * as before it — "Pago suscripcion FIS-A1B2C3" is at least as common as
     * "FIS-A1B2C3 pago suscripcion".
     */
    const tagged = upper.match(/FIS[\s-]*([A-Z0-9]{6})/);
    if (tagged)
        return `FIS-${canonicalise(tagged[1])}`;
    /**
     * A BARE code counts only when the whole field IS the code. This is the line
     * that stops the function inventing references out of prose: taking the
     * first six alphanumerics of anything would turn "pago suscripcion" into
     * FIS-PAG05U and "FACTURA123456" into FIS-FACTUR — values that then read as
     * a reference for SOMEONE ELSE'S checkout and, because a present-but-wrong
     * reference is a hard mismatch, would hold or reject a perfectly good
     * receipt. Silence must stay silence.
     */
    const bare = upper.replace(/[^A-Z0-9]/g, "");
    if (bare.length === 6)
        return `FIS-${canonicalise(bare)}`;
    return "";
}
/** Collapses the characters a human transcribes interchangeably. */
function canonicalise(six) {
    return six
        .split("")
        .map((ch) => CONFUSABLE[ch] ?? ch)
        .join("");
}
/**
 * Does what the payer wrote identify this checkout?
 *
 * Compares both sides through the same normalisation, so an O-for-0 in the
 * REFERENCE and an O-for-0 in the id land on the same string.
 */
export function planCheckoutPaymentReferenceMatches(checkoutId, rawReference) {
    const expected = planCheckoutPaymentReference(checkoutId);
    if (expected === "")
        return false;
    const normalisedExpected = normalisePlanCheckoutPaymentReference(expected);
    const given = normalisePlanCheckoutPaymentReference(rawReference);
    return given !== "" && given === normalisedExpected;
}
